import React, { ReactNode, useEffect, useMemo, useState } from "react";
import s from "./MobileHeader.module.scss";
import { AutoComplete, Input, Spin } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { getApiLanguage, publicRequest } from "../../utils/utils";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { AppDispatch, RootState } from "../../redux/store";
import { setLang } from "../../redux/InformationSlice";
import i18n from "../../i18n/i18n";

type HeaderProps = {
  onContactsClick: () => void;
};

interface MenuItem {
  key: string;
  label: string;
  path?: string;
  children?: MenuItem[];
}

const MobileHeader = ({ onContactsClick }: HeaderProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [closingKeys, setClosingKeys] = useState<string[]>([]);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [searchOptions, setSearchOptions] = useState<
    Array<{ value: string; label: ReactNode }>
  >([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const { t } = useTranslation();
  const language = useSelector((state: RootState) => state.info.lang) || "ru";
  const dispatch: AppDispatch = useDispatch();

  // Функция для закрытия меню
  const closeMenu = () => {
    setIsModalOpen(false);
    setOpenKeys([]);
    setClosingKeys([]);
  };

  const changeLanguage = (lang: "ru" | "kg" | "uz" | "tj") => {
    i18n.changeLanguage(lang);
    dispatch(setLang(lang));
    closeMenu();
  };

  const menuItems: MenuItem[] = [
    {
      key: "ru-laws",
      label: t("Законы России"),
      children: [
        {
          key: "ru-medical",
          label: t("Обязательное медицинское освидетельствование"),
          children: [
            { key: "/russia/hiv/6", label: t("ВИЧ"), path: "/russia/hiv/6" },
            {
              key: "/russia/pas/4",
              label: t("Употребление психоактивных веществ"),
              path: "/russia/pas/4",
            },
            {
              key: "/russia/syphilis/2",
              label: t("Сифилис"),
              path: "/russia/syphilis/2",
            },
            {
              key: "/russia/tuberculosis/3",
              label: t("Туберкулёз"),
              path: "/russia/tuberculosis/3",
            },
          ],
        },
        {
          key: "ru-hiv-migrants",
          label: t("Для мигрантов, живущих с ВИЧ"),
          children: [
            {
              key: "/russia/hiv/law-exceptions/1",
              label: t("Законное пребывание"),
              path: "/russia/hiv/law-exceptions/1",
            },
            {
              key: "/russia/hiv/visits/2",
              label: t("Краткосрочные визиты"),
              path: "/russia/hiv/visits/2",
            },
            {
              key: "/russia/hiv/migrants/trip/1",
              label: t("Подготовка к поездке"),
              path: "/russia/hiv/migrants/trip/1",
            },
            {
              key: "/russia/hiv/migrants/treatment/2",
              label: t("Получение лечения"),
              path: "/russia/hiv/migrants/treatment/2",
            },
            {
              key: "/russia/hiv/migrants/analysis/3",
              label: t("Анализы в России"),
              path: "/russia/hiv/migrants/analysis/3",
            },
          ],
        },
        {
          key: "/russia/pre-departure/tuberculosis/3",
          label: t("Для мигрантов с диагнозом туберкулез"),
          path: "/russia/pre-departure/tuberculosis/3",
        },
        {
          key: "/russia/pre-departure/syphilis/2",
          label: t("Для мигрантов с диагнозом сифилис"),
          path: "/russia/pre-departure/syphilis/2",
        },
        {
          key: "/russia/pre-departure/pas/4",
          label: t("Об употреблении психоактивных веществ"),
          path: "/russia/pre-departure/pas/4",
        },
      ],
    },
    {
      key: "kz-law",
      label: t("Законы Казахстана"),
      children: [
        {
          key: "/kazakhstan",
          label: t("Заболевания, ограничивающие въезд"),
          path: "/kazakhstan",
        },
        {
          key: "kz-hiv-migrants",
          label: t("Для мигрантов, живущих с ВИЧ"),
          children: [
            {
              key: "/kazakhstan/hiv/trip/1",
              label: t("Подготовка к поездке"),
              path: "/kazakhstan/hiv/trip/1",
            },
            {
              key: "/kazakhstan/hiv/treatment/2",
              label: t("Получение лечения"),
              path: "/kazakhstan/hiv/treatment/2",
            },
            {
              key: "/kazakhstan/hiv/analysis/3",
              label: t("Анализы в Казахстане"),
              path: "/kazakhstan/hiv/analysis/3",
            },
          ],
        },
        {
          key: "/kazakhstan/tuberculosis/3",
          label: t("Для мигрантов с диагнозом туберкулез"),
          path: "/kazakhstan/tuberculosis/3",
        },
        {
          key: "/kazakhstan/syphilis/2",
          label: t("Для мигрантов с диагнозом сифилис"),
          path: "/kazakhstan/syphilis/2",
        },
        {
          key: "/kazakhstan/pas/4",
          label: t("Об употреблении психоактивных веществ"),
          path: "/kazakhstan/pas/4",
        },
      ],
    },
    {
      key: "other-countries",
      label: t("Другие страны"),
      children: [
        {
          key: "/otherCountries/advises/2",
          label: t("Путешествия и миграция с ВИЧ"),
          path: "/otherCountries/advises/2",
        },
        {
          key: "/otherCountries/deportation/1",
          label: t("Страны, депортирующие иностранцев, живущих с ВИЧ"),
          path: "/otherCountries/deportation/1",
        },
        {
          key: "/otherCountries/restrictions",
          label: t("Ограничения на въезд для мигрантов, живущих с ВИЧ"),
          path: "/otherCountries/restrictions",
        },
      ],
    },
    {
      key: "info-block",
      label: t("Информационный блок"),
      children: [
        {
          key: "/about/diseases",
          label: t("О заболеваниях"),
          path: "/about/diseases",
        },
        {
          key: "checkup-locations",
          label: t("Где пройти обследования"),
          children: [
            {
              key: "/checkup/kyrgyzstan",
              label: t("Кыргызстан"),
              path: "/checkup/kyrgyzstan",
            },
            {
              key: "/checkup/tajikistan",
              label: t("Таджикистан"),
              path: "/checkup/tajikistan",
            },
            {
              key: "/checkup/uzbekistan",
              label: t("Узбекистан"),
              path: "/checkup/uzbekistan",
            },
          ],
        },
        {
          key: "/links",
          label: t("Полезные ссылки"),
          path: "/links",
        },
      ],
    },
  ];

  const staticPages = useMemo(
    () => [
      { label: t("Главная"), path: "/" },
      { label: t("Полезные ссылки"), path: "/links" },
      { label: t("Оценить риск"), path: "/risk" },
      { label: t("Пройти тест"), path: "/test" },
      { label: t("Я в России"), path: "/russia" },
      { label: t("Я в Казахстане"), path: "/kazakhstan" },
      { label: t("Я в другой стране"), path: "/otherCountries" },
    ],
    [t],
  );

  useEffect(() => {
    const q = searchValue.trim();
    if (q.length < 2) {
      setSearchOptions([]);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const handle = window.setTimeout(async () => {
      try {
        const { data } = await publicRequest.get("/api/v1/search/", {
          params: { q, lang: getApiLanguage(language), limit: 10 },
        });

        const apiResults: Array<{ label: string; path: string }> =
          data?.results ?? [];

        const merged = [
          ...apiResults.map((r) => ({ label: r.label, path: r.path })),
          ...staticPages,
        ];

        const seen = new Set<string>();
        const options = merged
          .filter((r) => {
            const hay = `${r.label} ${r.path}`.toLowerCase();
            if (!hay.includes(q.toLowerCase())) return false;
            const key = `${r.label}::${r.path}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          })
          .slice(0, 10)
          .map((r) => ({
            value: r.path,
            label: (
              <div className={s.searchOption}>
                <div className={s.searchOptionTitle}>{r.label}</div>
              </div>
            ),
          }));

        setSearchOptions(options);
      } catch (e) {
        // Fallback: local pages only
        const options = staticPages
          .filter((r) => r.label.toLowerCase().includes(q.toLowerCase()))
          .slice(0, 10)
          .map((r) => ({
            value: r.path,
            label: (
              <div className={s.searchOption}>
                <div className={s.searchOptionTitle}>{r.label}</div>
              </div>
            ),
          }));
        setSearchOptions(options);
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(handle);
  }, [searchValue, language, staticPages]);

  // Функция для переключения открытого состояния пункта меню с анимацией
  const toggleMenuItem = (key: string) => {
    if (openKeys.includes(key)) {
      // Закрываем с анимацией
      setClosingKeys([...closingKeys, key]);
      setTimeout(() => {
        setOpenKeys(openKeys.filter((k) => k !== key));
        setClosingKeys(closingKeys.filter((k) => k !== key));
      }, 300); // Должно совпадать с временем анимации в CSS
    } else {
      // Открываем
      setOpenKeys([...openKeys, key]);
    }
  };

  // Функция для рендеринга пунктов меню
  const renderMenuItems = (items: MenuItem[], level = 0) => {
    return items.map((item) => {
      const isOpen = openKeys.includes(item.key);
      const isClosing = closingKeys.includes(item.key);
      const hasChildren = item.children && item.children.length > 0;
      const paddingLeft = 16 + level * 16;

      if (hasChildren) {
        return (
          <div key={item.key} className={s.menu_item_container}>
            <div
              className={`${s.menu_item} ${isOpen ? s.menu_item_open : ""}`}
              style={{ paddingLeft: `${paddingLeft}px` }}
              onClick={() => toggleMenuItem(item.key)}
            >
              <span className={s.menu_item_label}>{item.label}</span>
              <span
                className={`${s.menu_arrow} ${isOpen ? s.menu_arrow_open : ""}`}
              >
                ▼
              </span>
            </div>
            <div
              className={`${s.menu_children} ${isOpen ? s.menu_children_open : ""} ${isClosing ? s.menu_children_closing : ""}`}
            >
              {renderMenuItems(item.children!, level + 1)}
            </div>
          </div>
        );
      } else {
        return (
          <div
            key={item.key}
            className={`${s.menu_item} ${s.menu_leaf}`}
            style={{ paddingLeft: `${paddingLeft}px` }}
            onClick={() => {
              if (item.path) {
                navigate(item.path);
                closeMenu();
              }
            }}
          >
            <span className={s.menu_item_label}>{item.label}</span>
          </div>
        );
      }
    });
  };

  return (
    <div className={s.mobile_header}>
      {/* Логотип */}
      <Link to="/" className={s.logo} onClick={() => setIsModalOpen(false)}>
        <img src="/images/Main.svg" className={s.logo_img} alt="Logo" />
      </Link>

      {/* Бургер */}
      <div className={s.menu} onClick={() => setIsModalOpen(true)}>
        <div />
        <div />
        <div />
      </div>

      {/* Модальное меню */}
      <div className={`${s.modal} ${isModalOpen ? s.open : ""}`}>
        <Link to="/" className={s.logo} onClick={closeMenu}>
          <img src="/images/Logo.svg" className={s.logo_img} alt="Logo" />
        </Link>

        {/* Кастомное меню */}
        <div className={s.custom_menu}>{renderMenuItems(menuItems)}</div>

        {/* Поиск */}
        <div className={s.search}>
          <AutoComplete
            value={searchValue}
            options={searchOptions}
            onSelect={(value) => {
              const path = String(value);
              setSearchValue("");
              setSearchOptions([]);
              if (path.startsWith("http://") || path.startsWith("https://")) {
                window.location.assign(path);
                return;
              }
              navigate(path);
            }}
            className={s.search_autocomplete}
            onSearch={(v) => setSearchValue(v)}
          >
            <Input
              prefix={
                searchLoading ? (
                  <Spin size="small" />
                ) : (
                  <img
                    src="/images/icon-search.svg"
                    className={s.search_icon}
                  />
                )
              }
              placeholder={t("Поиск")}
              className={s.search_input}
              allowClear
              variant="filled"
            />
          </AutoComplete>
        </div>

        <div className={s.ai_lang}>
          <div className={s.lang_buttons}>
            <div
              className={`${s.lang_button} ${
                language === "ru" ? s.lang_button_active : ""
              }`}
              onClick={() => changeLanguage("ru")}
            >
              RU
            </div>
            <div
              className={`${s.lang_button} ${
                language === "kg" ? s.lang_button_active : ""
              }`}
              onClick={() => changeLanguage("kg")}
            >
              KG
            </div>
            <div
              className={`${s.lang_button} ${
                language === "uz" ? s.lang_button_active : ""
              }`}
              onClick={() => changeLanguage("uz")}
            >
              UZ
            </div>
            <div
              className={`${s.lang_button} ${
                language === "tj" ? s.lang_button_active : ""
              }`}
              onClick={() => changeLanguage("tj")}
            >
              TJ
            </div>
          </div>
        </div>

        {/* Закрытие модального */}
        <div className={s.back_icon} onClick={closeMenu}>
          <img src="/images/Menu burger.svg" className={s.menu_icon} alt="" />
        </div>
      </div>

      {/* Оверлей для закрытия по клику вне меню */}
      {isModalOpen && <div className={s.modal_overlay} onClick={closeMenu} />}
    </div>
  );
};

export default MobileHeader;
